/*
  ARAÇ HASAR VE DEĞER KAYBI HESAP MOTORU
  ======================================
  Kullanıcı (avukat) 2026-10-04'te istedi: araç bilgileri, km, değişen /
  onarılan parçalar, önceki hasar kaydı girilerek maddi hasar ve değer
  kaybı hesaplansın; yalnızca örnek bilirkişi raporlarına değil resmî
  kaynaklara dayansın.

  DAYANAKLAR (sayfada da gösteriliyor, bkz. DAYANAKLAR):
  - Değer kaybı = kaza öncesi hasarsız ikinci el değer − onarım sonrası
    ikinci el değer. KTK ZMSS Genel Şartları A.5 (RG 12.06.2026/33278,
    yürürlük 01.07.2026); Yargıtay 17. HD 2016/966 E. 2016/5728 K.,
    4. HD 2021/18000 E. 2021/5904 K.
  - Eski formül (Ek-1, 4.12.2021 değişik; 01.07.2026'da kaldırıldı):
    DK = Piyasa Değeri × R × K × H × G. Yalnızca KARŞILAŞTIRMA için
    hesaplanır; AYM 17.07.2020 2019/40 E. 2020/40 K. kararından sonra
    mahkemeyi bağlamaz.
  - Hasar KDV dahil (fatura aranmaz), araç mahrumiyeti trafik sigortası
    teminatı dışında (işleten/sürücüye), sigortacı başvurudan 8 iş günü
    sonra temerrüde düşer (KTK m.99).

  Bütün tutarlar kuruşa yuvarlanır (bilirkişi raporlarıyla aynı).
*/

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** "12.345,67" / "12345.67" / 12345.67 → sayı */
export const sayi = (v) => {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  let s = String(v).trim().replace(/\s/g, '').replace(/TL$/i, '');
  if (!s) return 0;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

export const tl = (n) =>
  (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const yuzde = (n, basamak = 2) =>
  `%${(Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: basamak, maximumFractionDigits: basamak })}`;

/** Tutarı kutuya Türkçe biçimde yazar: 300000 -> "300.000", 12500.5 -> "12.500,50". */
export const tutarYaz = (v) => {
  if (String(v ?? '').trim() === '') return '';
  const n = sayi(v);
  const k = Number.isInteger(n) ? 0 : 2;
  return n.toLocaleString('tr-TR', { minimumFractionDigits: k, maximumFractionDigits: 2 });
};

/* ------------------------------------------------------------------
   ARAÇ KİMLİĞİ: TSB listesiyle eşleştirme ve ilan sitesi bağlantıları
   ------------------------------------------------------------------ */
const HARF = { ı: 'I', i: 'I', İ: 'I', î: 'I', Î: 'I', ş: 'S', Ş: 'S', ğ: 'G', Ğ: 'G', ü: 'U', Ü: 'U', û: 'U', Û: 'U', ö: 'O', Ö: 'O', ç: 'C', Ç: 'C', â: 'A', Â: 'A' };

/** Karşılaştırma için: Türkçe harfler sade, büyük harf, tire boşluk olur. "Hyundai İ20" → "HYUNDAI I20" */
export const katla = (s) => String(s ?? '')
  .replace(/[ıiİîÎşŞğĞüÜûÛöÖçÇâÂ]/g, (h) => HARF[h])
  .toUpperCase()
  .replace(/[-_]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

/** Ruhsattaki markanın TSB listesindeki karşılığı ("MERCEDES BENZ" → "MERCEDES-BENZ"). */
export const tsbMarkaBul = (markalar, ad) => {
  const h = katla(ad);
  if (!h || !Array.isArray(markalar)) return null;
  return markalar.find((m) => katla(m.ad) === h)
    || markalar.find((m) => h.startsWith(`${katla(m.ad)} `) || katla(m.ad).startsWith(`${h} `))
    || null;
};

/**
 * Ruhsat OCR'ında harf-rakam karışır ("T10X" → "TIOX", "T1OX"). Karşılaştırmada
 * O→0, I/L→1 sayılır; ekranda gösterilen adlar değişmez.
 */
export const ocrSade = (s) => katla(s).replace(/O/g, '0').replace(/[IL]/g, '1');

/** Paket süzgeci: süzgeç metni paket adında geçiyor mu (OCR karışıklığına dayanıklı). */
export const paketUyar = (paketAdi, suzgec) => {
  const s = ocrSade(suzgec);
  return !s || ocrSade(paketAdi).includes(s);
};

/**
 * Ruhsattaki ticari adın TSB paket listesindeki karşılığı.
 * Tam eşleşme ya da tek ve belirgin en iyi aday varsa "model" döner;
 * yoksa ortak kelimesi olan "adaylar" (ör. ruhsatta yalnız "ELANTRA").
 */
export const tsbModelBul = (modeller, ad) => {
  const h = ocrSade(ad);
  if (!h || !Array.isArray(modeller) || !modeller.length) return { model: null, adaylar: [] };
  const tam = modeller.find((m) => ocrSade(m.ad) === h);
  if (tam) return { model: tam, adaylar: [tam] };

  const kelimeler = h.split(' ');
  const puanli = modeller
    .map((m) => {
      const mk = ocrSade(m.ad).split(' ');
      const ortak = kelimeler.filter((k) => mk.includes(k)).length;
      return { m, ortak, puan: ortak / Math.max(kelimeler.length, mk.length) };
    })
    .filter((x) => x.ortak > 0)
    .sort((a, b) => b.puan - a.puan);
  const tek = puanli.length && puanli[0].puan >= 0.75 && (puanli.length === 1 || puanli[1].puan < puanli[0].puan)
    ? puanli[0].m : null;
  return { model: tek, adaylar: puanli.map((x) => x.m) };
};

const slug = (s) => katla(s).toLowerCase().replace(/\./g, '-').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

/**
 * Emsal araştırması için ilan sitesi bağlantıları.
 * arabam.com: aynı paket, aynı model yılı, km ±%25. Paket adı sitede yoksa
 *   arabam kendisi modelin genel sayfasına yönlendiriyor (denendi).
 * sahibinden.com: modelin sayfası (yıl ve km'yi sitede seçmek gerekiyor;
 *   site otomatik erişime kapalı olduğu için süzgeç adları doğrulanamadı).
 */
export const ilanBaglantilari = ({ marka, model, yil, km }) => {
  if (!String(marka || '').trim() || !String(model || '').trim()) return null;
  const q = new URLSearchParams();
  const y = Number(yil);
  if (y >= 1950 && y <= 2100) { q.set('minYear', String(y)); q.set('maxYear', String(y)); }
  const k = sayi(km);
  if (k > 0) {
    q.set('minkm', String(Math.max(0, Math.floor((k * 0.75) / 5000) * 5000)));
    q.set('maxkm', String(Math.ceil((k * 1.25) / 5000) * 5000));
  }
  const seri = katla(model).split(' ')[0];
  const sorgu = q.toString();
  return {
    arabam: `https://www.arabam.com/ikinci-el/otomobil/${slug(`${marka} ${model}`)}${sorgu ? `?${sorgu}` : ''}`,
    sahibinden: `https://www.sahibinden.com/${slug(`${marka} ${seri}`)}`,
  };
};

/* ------------------------------------------------------------------
   ARAÇ GRUPLARI (Ek-1 Madde 1 tablosu)
   ------------------------------------------------------------------ */
export const ARAC_GRUPLARI = [
  { id: 'otomobil', ad: 'Otomobil', kod: 'A' },
  { id: 'taksi', ad: 'Taksi', kod: 'A' },
  { id: 'minibus', ad: 'Minibüs', kod: 'B' },
  { id: 'otobus', ad: 'Otobüs', kod: 'B' },
  { id: 'kamyonet', ad: 'Kamyonet', kod: 'C' },
  { id: 'kamyon', ad: 'Kamyon', kod: 'C' },
  { id: 'cekici', ad: 'Çekici', kod: 'C' },
  { id: 'isMakinesi', ad: 'İş makinesi', kod: 'D' },
  { id: 'traktor', ad: 'Traktör', kod: 'D' },
  { id: 'tarimMakinesi', ad: 'Tarım makinesi', kod: 'D' },
  { id: 'ozelAmacli', ad: 'Özel amaçlı araç', kod: 'Ç' },
  { id: 'tanker', ad: 'Tanker', kod: 'Ç' },
  { id: 'romork', ad: 'Römork', kod: 'E' },
  { id: 'motosiklet', ad: 'Motosiklet', kod: 'F' },
];
export const grupKodu = (grupId) => (ARAC_GRUPLARI.find((g) => g.id === grupId) || ARAC_GRUPLARI[0]).kod;

/* ------------------------------------------------------------------
   Ek-1 TABLOLARI (4.12.2021 değişik metin; RG 31679)
   [altSinir, ustSinir(dahil), katsayi]
   ------------------------------------------------------------------ */
const R1 = [[0, 49999, 0.65], [50000, 99999, 0.70], [100000, 199999, 0.75], [200000, 299999, 0.80],
  [300000, 399999, 0.85], [400000, 499999, 0.90], [500000, 749999, 0.95], [750000, Infinity, 1.00]];
const R2 = [[0, 249999, 0.65], [250000, 349999, 0.70], [350000, 499999, 0.75], [500000, 749999, 0.80],
  [750000, 999999, 0.85], [1000000, 1249999, 0.90], [1250000, 1499999, 0.95], [1500000, Infinity, 1.00]];
const K1 = [[0, 19999, 1.00], [20000, 49999, 0.95], [50000, 99999, 0.90], [100000, 149999, 0.85],
  [150000, 199999, 0.80], [200000, 299999, 0.75], [300000, Infinity, 0.70]];
const K2 = [[0, 49999, 1.00], [50000, 149999, 0.95], [150000, 299999, 0.90], [300000, 499999, 0.85],
  [500000, 749999, 0.80], [750000, 999999, 0.75], [1000000, Infinity, 0.70]];
const K3 = [[0, 500, 1.00], [501, 1000, 0.95], [1001, 2000, 0.90], [2001, 3000, 0.85],
  [3001, 4000, 0.80], [4001, 5000, 0.75], [5001, Infinity, 0.70]];

const tablodan = (tablo, deger) => {
  const v = Math.max(0, Number(deger) || 0);
  // Tablolar tam sayı sınırlı; ara değer (ör. 49.999,5) bir sonraki dilime girer.
  const satir = tablo.find(([alt, ust]) => v >= alt && v <= ust) || tablo.find(([alt, ust]) => v >= alt && v < ust + 1);
  return satir || tablo[tablo.length - 1];
};

export const rKatsayisi = (kod, piyasaDegeri) =>
  tablodan(kod === 'A' || kod === 'F' ? R1 : R2, piyasaDegeri)[2];

export const kTablosu = (kod) => (kod === 'D' ? K3 : (kod === 'A' || kod === 'F' ? K1 : K2));
export const kKatsayisi = (kod, kmVeyaSaat) => tablodan(kTablosu(kod), kmVeyaSaat)[2];

/**
 * G.3: "Tablo K.1 ve K.2'de kilometre alt sınırına 1000 km.ye kadar yakınlık +0,05".
 * Yorum: araç bir alt dilime yeni geçmişse (dilimin alt sınırını en çok
 * 1000 km aşmışsa) katsayı keskin düşmesin diye +0,05 eklenir. İlk dilimde
 * (alt sınır 0) uygulanmaz. İş makinelerinde (K.3) uygulanmaz.
 */
export const g3Uygulanir = (kod, km) => {
  if (kod === 'D') return false;
  const [alt] = tablodan(kTablosu(kod), km);
  const v = Number(km) || 0;
  return alt > 0 && v - alt <= 1000;
};

/* ------------------------------------------------------------------
   PARÇA KATALOĞU
   Ek-1 Madde 4 tablosu, A (otomobil / taksi) grubu:
     P = parça değişim, O = onarım (hafif/orta/yüksek), Y = boya (tam/lokal)
   Listede olmayan parçalar (tampon, far, cam, jant...) Ek-1'de katsayı
   almaz; Yargıtay uygulamasında da boyasız, vidayla sökülüp takılan
   parçalar tek başına değer kaybı doğurmaz kabul edilir.
   ------------------------------------------------------------------ */
const ek1 = (kod, ad, bolge, P, O, Y) => ({ kod, ad, bolge, P, O, Y, ek1: true });
export const PARCALAR = [
  ek1('A.1', 'Tavan sacı', 'Tavan ve gövde', 5.00, [1.00, 1.50, 2.00], [3.00, 1.50]),
  ek1('A.2', 'Ön panel (sac)', 'Ön', 1.00, [0.50, 1.00, 1.50], [0.50, 0.25]),
  ek1('A.3', 'Sağ ön çamurluk (sac)', 'Sağ yan', 1.00, [0.50, 0.75, 1.00], [1.00, 0.50]),
  ek1('A.4', 'Sol ön çamurluk (sac)', 'Sol yan', 1.00, [0.50, 0.75, 1.00], [1.00, 0.50]),
  ek1('A.5', 'Sağ ön podye sacı', 'Ön', 2.00, [0.50, 0.75, 1.00], [0.50, 0.25]),
  ek1('A.6', 'Sol ön podye sacı', 'Ön', 2.00, [0.50, 0.75, 1.00], [0.50, 0.25]),
  ek1('A.7', 'Sağ şase ön', 'Şase ve yapı', 3.00, [1.00, 1.50, 2.00], [0.50, 0.25]),
  ek1('A.8', 'Sol şase ön', 'Şase ve yapı', 3.00, [1.00, 1.50, 2.00], [0.50, 0.25]),
  ek1('A.9', 'Göğüs sacı', 'Ön', 4.00, [1.00, 1.50, 2.00], [0.50, 0.25]),
  ek1('A.10', 'Motor kaputu', 'Ön', 1.00, [0.50, 0.75, 1.00], [1.00, 0.50]),
  ek1('A.11', 'Sağ ön kapı (kapı sacı)', 'Sağ yan', 1.00, [0.50, 0.75, 1.00], [1.00, 0.50]),
  ek1('A.12', 'Sol ön kapı (kapı sacı)', 'Sol yan', 1.00, [0.50, 0.75, 1.00], [1.00, 0.50]),
  ek1('A.13', 'Sağ arka kapı (kapı sacı)', 'Sağ yan', 1.00, [0.50, 0.75, 1.00], [1.00, 0.50]),
  ek1('A.14', 'Sol arka kapı (kapı sacı)', 'Sol yan', 1.00, [0.50, 0.75, 1.00], [1.00, 0.50]),
  ek1('A.15', 'Sağ marşpiyel (sac)', 'Sağ yan', 2.00, [0.50, 0.75, 1.00], [0.50, 0.25]),
  ek1('A.16', 'Sol marşpiyel (sac)', 'Sol yan', 2.00, [0.50, 0.75, 1.00], [0.50, 0.25]),
  ek1('A.17', 'A direği sağ', 'Tavan ve gövde', 1.00, [0.50, 0.75, 1.00], [0.50, 0.25]),
  ek1('A.18', 'B direği sağ', 'Tavan ve gövde', 2.00, [0.50, 0.75, 1.00], [0.50, 0.25]),
  ek1('A.19', 'A direği sol', 'Tavan ve gövde', 1.00, [0.50, 0.75, 1.00], [0.50, 0.25]),
  ek1('A.20', 'B direği sol', 'Tavan ve gövde', 2.00, [0.50, 0.75, 1.00], [0.50, 0.25]),
  ek1('A.21', 'Bagaj kapağı', 'Arka', 1.00, [0.50, 1.00, 1.50], [1.00, 0.50]),
  ek1('A.22', 'Arka panel', 'Arka', 2.00, [0.50, 1.00, 1.50], [1.00, 0.50]),
  ek1('A.23', 'Sağ arka çamurluk', 'Sağ yan', 4.00, [0.50, 1.00, 1.50], [1.00, 0.50]),
  ek1('A.24', 'Sol arka çamurluk', 'Sol yan', 4.00, [0.50, 1.00, 1.50], [1.00, 0.50]),
  ek1('A.25', 'Havuz sacı (bagaj tabanı)', 'Arka', 3.00, [0.50, 1.00, 1.50], [0.50, 0.25]),
  ek1('A.26', 'Sağ şase arka', 'Şase ve yapı', 3.00, [1.00, 1.50, 2.00], [0.50, 0.25]),
  ek1('A.27', 'Sol şase arka', 'Şase ve yapı', 3.00, [1.00, 1.50, 2.00], [0.50, 0.25]),
  ek1('A.28', 'Motor traversi / dingil', 'Şase ve yapı', 1.00, [1.00, 1.50, 2.00], null),
  ek1('A.29', 'Yolcu hava yastığı', 'Hava yastıkları', 2.00, null, null),
  ek1('A.30', 'Sürücü hava yastığı', 'Hava yastıkları', 2.00, null, null),
  ek1('A.31', 'Sağ yan hava yastığı', 'Hava yastıkları', 2.00, null, null),
  ek1('A.32', 'Sol yan hava yastığı', 'Hava yastıkları', 2.00, null, null),
  // Ek-1 listesinde olmayan, sık hasar gören parçalar
  ...[
    ['X.1', 'Ön tampon', 'Ön', 'vidali'], ['X.2', 'Arka tampon', 'Arka', 'vidali'],
    ['X.3', 'Tampon braketi / bağlantı', 'Ön', 'vidali'], ['X.4', 'Ön ızgara (panjur)', 'Ön', 'vidali'],
    ['X.5', 'Sağ far', 'Ön', 'vidali'], ['X.6', 'Sol far', 'Ön', 'vidali'], ['X.7', 'Sis farı', 'Ön', 'vidali'],
    ['X.8', 'Sağ stop lambası', 'Arka', 'vidali'], ['X.9', 'Sol stop lambası', 'Arka', 'vidali'],
    ['X.10', 'Sağ dikiz aynası', 'Sağ yan', 'vidali'], ['X.11', 'Sol dikiz aynası', 'Sol yan', 'vidali'],
    ['X.12', 'Ön cam', 'Camlar', 'vidali'], ['X.13', 'Arka cam', 'Camlar', 'vidali'], ['X.14', 'Yan cam', 'Camlar', 'vidali'],
    ['X.15', 'Jant', 'Mekanik ve yürüyen aksam', 'vidali'], ['X.16', 'Lastik', 'Mekanik ve yürüyen aksam', 'vidali'],
    ['X.17', 'Radyatör / kondenser', 'Mekanik ve yürüyen aksam', 'mekanik'],
    ['X.18', 'Süspansiyon (salıncak, amortisör, kol)', 'Mekanik ve yürüyen aksam', 'mekanik'],
    ['X.19', 'Rot / rotbaşı / aks', 'Mekanik ve yürüyen aksam', 'mekanik'],
    ['X.20', 'Fren aksamı', 'Mekanik ve yürüyen aksam', 'mekanik'],
    ['X.21', 'Motor / şanzıman parçası', 'Mekanik ve yürüyen aksam', 'mekanik'],
    ['X.22', 'Batarya / yüksek gerilim aksamı (elektrikli)', 'Mekanik ve yürüyen aksam', 'mekanik'],
    ['X.23', 'Sensör / kamera / elektronik', 'Elektrik', 'mekanik'],
    ['X.24', 'Çamurluk davlumbazı / iç kaplama', 'Diğer', 'vidali'],
    ['X.25', 'Diğer parça', 'Diğer', 'diger'],
  ].map(([kod, ad, bolge, tur]) => ({ kod, ad, bolge, tur, ek1: false })),
];
export const BOLGELER = ['Ön', 'Sağ yan', 'Sol yan', 'Arka', 'Tavan ve gövde', 'Şase ve yapı',
  'Hava yastıkları', 'Camlar', 'Mekanik ve yürüyen aksam', 'Elektrik', 'Diğer'];
export const parcaBul = (kod) => PARCALAR.find((p) => p.kod === kod);

/**
 * Değer kaybına muhtemel etkisi (yalnızca gösterge). Ek-1 değişim
 * katsayısından türetilir: ≥3 yapısal (yüksek), 2 orta-yüksek, 1 kaporta;
 * Ek-1 dışı parçalar "sınırlı".
 */
export const etkiDuzeyi = (parca) => {
  if (!parca) return { id: 'yok', ad: '-' };
  if (!parca.ek1) {
    if (parca.tur === 'mekanik') return { id: 'mekanik', ad: 'Mekanik — tek başına sınırlı' };
    return { id: 'sinirli', ad: 'Vidalı / söküp takılan — tek başına sınırlı' };
  }
  if (parca.P >= 3) return { id: 'yuksek', ad: 'Yapısal — yüksek' };
  if (parca.P >= 2) return { id: 'ortaYuksek', ad: 'Orta–yüksek' };
  return { id: 'orta', ad: 'Kaporta — orta' };
};

export const ISLEMLER = [
  { id: 'degisim', ad: 'Değişim' },
  { id: 'onarim', ad: 'Onarım' },
  { id: 'boya', ad: 'Yalnız boya' },
  { id: 'sokTak', ad: 'Söküp takma' },
];
export const ONARIM_SEVIYELERI = [
  { id: 'hafif', ad: 'Hafif' }, { id: 'orta', ad: 'Orta' }, { id: 'yuksek', ad: 'Yüksek' },
];
export const BOYALAR = [{ id: 'tam', ad: 'Tam boya' }, { id: 'lokal', ad: 'Lokal boya' }, { id: 'yok', ad: 'Boyasız' }];

/** Ek-1 m.4/2: işçilik ÷ parça (KDV hariç, iskontosuz) ≤%15 hafif, ≤%30 orta, üstü yüksek. */
export const onarimSeviyesiOner = (parcaBedeli, iscilikBedeli) => {
  const p = Number(parcaBedeli) || 0;
  const i = Number(iscilikBedeli) || 0;
  if (p <= 0 || i <= 0) return null;
  const oran = (i / p) * 100;
  return { oran, seviye: oran <= 15 ? 'hafif' : oran <= 30 ? 'orta' : 'yuksek' };
};

/* ------------------------------------------------------------------
   1) MADDİ HASAR (ONARIM BEDELİ)
   Sıra bilirkişi raporlarındaki gibi:
   parça toplamı − iskonto − kıymet kazanma tenzili + işçilik = KDV hariç
   → + KDV = toplam hasar → × karşı tarafın kusuru → − ödenen = bakiye
   ------------------------------------------------------------------ */
export function maddiHasarHesapla({
  kalemler = [],          // [{ bedel, adet }]  KDV hariç parça bedelleri
  iskontoOrani = 0,       // %
  kiymetKazanma = 0,      // TL (KDV hariç) — yeni parçanın eskisine göre değer artışı
  iscilikler = {},        // { kaporta, boya, mekanik, elektrik, diger } KDV hariç
  kdvOrani = 20,
  kdvDahil = true,
  kusurOrani = 100,       // karşı tarafın kusuru %
  odenen = 0,             // sigortanın / karşı tarafın ödediği hasar (KDV dahil)
  // Parça fiyatları bilinmiyor, yalnız "parça + işçilik" toplamı biliniyorsa: kalemler,
  // iskonto ve işçilik kullanılmaz; kıymet kazanma bu toplamın KDV hariç tutarından düşülür.
  birlesikToplam = null,
  birlesikKdvIcinde = false, // toplam KDV dahil bildirildi; KDV hariç tutar oranla ayrılır
} = {}) {
  const r = Number(kdvOrani) || 0;
  const toplamGirisi = birlesikToplam != null;
  const parcaToplam = toplamGirisi ? 0 : round2(kalemler.reduce((t, k) => t + (Number(k.bedel) || 0) * (Number(k.adet) || 1), 0));
  const iskonto = toplamGirisi ? 0 : round2(parcaToplam * (Number(iskontoOrani) || 0) / 100);
  const iscilikToplam = toplamGirisi ? 0 : round2(Object.values(iscilikler).reduce((t, v) => t + (Number(v) || 0), 0));
  const birlesikGirilen = toplamGirisi ? round2(Math.max(0, Number(birlesikToplam) || 0)) : 0;
  const kdvIcinde = toplamGirisi && !!birlesikKdvIcinde;
  const birlesik = kdvIcinde ? round2(birlesikGirilen / (1 + r / 100)) : birlesikGirilen; // KDV hariç
  const kiymet = round2(Math.min(Number(kiymetKazanma) || 0, toplamGirisi ? birlesik : parcaToplam - iskonto));
  const parcaNet = toplamGirisi ? 0 : round2(parcaToplam - iskonto - kiymet);
  const kdvHaric = toplamGirisi ? round2(birlesik - kiymet) : round2(parcaNet + iscilikToplam);
  // KDV dahil girilen toplamda indirim yoksa sonuç girilen tutarın kendisidir (kuruş farkı çıkmasın).
  const kdv = !kdvDahil ? 0 : (kdvIcinde && !kiymet ? round2(birlesikGirilen - kdvHaric) : round2(kdvHaric * r / 100));
  const toplam = round2(kdvHaric + kdv);
  const kusurluTutar = round2(toplam * clampYuzde(kusurOrani) / 100);
  const odenenT = round2(Number(odenen) || 0);
  const bakiye = round2(Math.max(0, kusurluTutar - odenenT));
  // Ek-1 Hasar Tutarı Katsayısı için: "toplam hasar tutarı üzerinden hiçbir şekilde tenzil yapılmaz"
  const brutKdvDahil = kdvIcinde && kdvDahil
    ? birlesikGirilen
    : round2((toplamGirisi ? birlesik : parcaToplam + iscilikToplam) * (1 + (kdvDahil ? r : 0) / 100));
  return {
    toplamGirisi, birlesik, birlesikGirilen, birlesikKdvIcinde: kdvIcinde,
    parcaToplam, iskonto, kiymet, parcaNet, iscilikToplam, kdvHaric, kdv, toplam,
    kusurOrani: clampYuzde(kusurOrani), kusurluTutar, odenen: odenenT, bakiye, brutKdvDahil,
  };
}

const clampYuzde = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 100;
  return Math.min(100, Math.max(0, n));
};

const ortalama = (dizi) => {
  const d = dizi.filter((x) => Number(x) > 0).map(Number);
  return d.length ? d.reduce((a, b) => a + b, 0) / d.length : 0;
};

/** Araştırma tarihindeki fiyatı kaza tarihine indirger: × (TÜFE kaza / TÜFE araştırma). */
export const endeksKatsayisi = (tufeKaza, tufeArastirma) => {
  const a = Number(tufeKaza) || 0;
  const b = Number(tufeArastirma) || 0;
  return a > 0 && b > 0 ? a / b : 1;
};

/* ------------------------------------------------------------------
   2) DEĞER KAYBI — PİYASA (EMSAL) YÖNTEMİ  ← ESAS YÖNTEM
   ------------------------------------------------------------------ */
export function degerKaybiPiyasa({
  emsaller = [],           // [{ kaynak, hasarsiz, hasarli }]
  hasarsizDogrudan = 0,    // emsal tablosu yerine doğrudan değer
  hasarliDogrudan = 0,
  endeks = 1,              // fiyatları kaza tarihine indirgeme katsayısı
  kusurOrani = 100,
  odenen = 0,
  hasarTutari = 0,         // oran göstergesi için (KDV dahil toplam hasar)
} = {}) {
  const hasarsizlar = emsaller.map((e) => sayi(e.hasarsiz)).filter((x) => x > 0);
  const hasarlilar = emsaller.map((e) => sayi(e.hasarli)).filter((x) => x > 0);
  const hasarsizArastirma = hasarsizlar.length ? ortalama(hasarsizlar) : sayi(hasarsizDogrudan);
  const hasarliArastirma = hasarlilar.length ? ortalama(hasarlilar) : sayi(hasarliDogrudan);
  const k = Number(endeks) > 0 ? Number(endeks) : 1;
  const hasarsiz = round2(hasarsizArastirma * k);
  const hasarli = round2(hasarliArastirma * k);
  const dk = hasarsiz > 0 && hasarli > 0 ? round2(Math.max(0, hasarsiz - hasarli)) : 0;
  const kusurluTutar = round2(dk * clampYuzde(kusurOrani) / 100);
  const odenenT = round2(Number(odenen) || 0);
  return {
    yontem: 'emsal',
    hasarsizAdet: hasarsizlar.length,
    hasarliAdet: hasarlilar.length,
    hasarsizArastirma: round2(hasarsizArastirma),
    hasarliArastirma: round2(hasarliArastirma),
    endeks: k,
    hasarsiz,
    hasarli,
    dk,
    dkOrani: hasarsiz > 0 ? (dk / hasarsiz) * 100 : 0,
    hasarOrani: hasarsiz > 0 && hasarTutari > 0 ? (hasarTutari / hasarsiz) * 100 : 0,
    kusurOrani: clampYuzde(kusurOrani),
    kusurluTutar,
    odenen: odenenT,
    bakiye: round2(Math.max(0, kusurluTutar - odenenT)),
    eksik: !(hasarsiz > 0) ? 'hasarsiz' : !(hasarli > 0) ? 'hasarli' : null,
  };
}

/* ------------------------------------------------------------------
   3) DEĞER KAYBI — ESKİ GENEL ŞARTLAR Ek-1 (yalnız karşılaştırma)
   DK = Piyasa Değeri × R × K × H × G   (F kodu: × 2,5)
   HK = Σ (Pᵢ + Oᵢ + Yᵢ);  T = (Hasar Tutarı ÷ Piyasa Değeri × 100) × 0,10
   H = (HK + T) ÷ 100;     G = 1 + (G.1 + G.2 + G.3)
   ------------------------------------------------------------------ */
export function degerKaybiEk1({
  grupId = 'otomobil',
  tsbDegeri = 0,           // TSB Kasko Araç Değer Listesi
  tobbDegeri = 0,          // TOBB SEİK Piyasa Değer Listesi
  arastirmaDegeri = 0,     // ikisi de yoksa eksperin araştırdığı değer
  arastirmaKaynak = 'Değer araştırması',
  kmVeyaSaat = 0,
  hasarliParcalar = [],    // [{ kod, islem, onarimSeviyesi, boya, oncekiHasar }]
  hasarTutari = 0,         // KDV dahil, tenzilsiz
  ticari = false,
  sbmKayitSayisi = 0,
} = {}) {
  const kod = grupKodu(grupId);
  const tsb = sayi(tsbDegeri);
  const tobb = sayi(tobbDegeri);
  let piyasa;
  let piyasaKaynak;
  if (tsb > 0 && tobb > 0) { piyasa = (tsb + tobb) / 2; piyasaKaynak = 'TSB ve TOBB listelerinin ortalaması'; }
  else if (tsb > 0) { piyasa = tsb; piyasaKaynak = 'TSB Kasko Araç Değer Listesi'; }
  else if (tobb > 0) { piyasa = tobb; piyasaKaynak = 'TOBB SEİK Piyasa Değer Listesi'; }
  else { piyasa = sayi(arastirmaDegeri); piyasaKaynak = arastirmaKaynak; }
  piyasa = round2(piyasa);

  const R = piyasa > 0 ? rKatsayisi(kod, piyasa) : 0;
  const K = kKatsayisi(kod, kmVeyaSaat);
  const parcaListesiVar = kod === 'A';

  const satirlar = hasarliParcalar.map((h) => {
    const p = parcaBul(h.kod);
    if (!p || !p.ek1 || !parcaListesiVar) return { kod: h.kod, ad: p ? p.ad : h.kod, P: 0, O: 0, Y: 0, toplam: 0, not: p && !p.ek1 ? 'Ek-1 listesinde yok' : '' };
    let P = 0; let O = 0; let Y = 0;
    if (h.islem === 'degisim') P = p.P || 0;
    if (h.islem === 'onarim' && p.O) {
      const i = h.onarimSeviyesi === 'hafif' ? 0 : h.onarimSeviyesi === 'orta' ? 1 : 2;
      O = p.O[i];
    }
    if ((h.islem === 'degisim' || h.islem === 'onarim' || h.islem === 'boya') && p.Y && h.boya !== 'yok') {
      Y = h.boya === 'lokal' ? p.Y[1] : p.Y[0];
    }
    return { kod: p.kod, ad: p.ad, P, O, Y, toplam: P + O + Y, not: '' };
  });
  const HK = round2(satirlar.reduce((t, s) => t + s.toplam, 0));
  const T = piyasa > 0 ? (sayi(hasarTutari) / piyasa) * 100 * 0.10 : 0;
  const H = (HK + T) / 100;
  const G1 = ticari ? -0.05 : 0;
  const G2 = -Math.min(0.15, 0.03 * Math.max(0, Math.floor(Number(sbmKayitSayisi) || 0)));
  const G3 = g3Uygulanir(kod, kmVeyaSaat) ? 0.05 : 0;
  const G = 1 + G1 + G2 + G3;
  let DK = piyasa * R * K * H * G;
  if (kod === 'F') DK *= 2.5;
  return {
    kod, piyasa, piyasaKaynak, R, K, satirlar, HK, T, H, G1, G2, G3, G,
    DK: round2(Math.max(0, DK)),
    parcaListesiVar,
  };
}

/* ------------------------------------------------------------------
   3b) DEĞER KAYBI — EMSAL YOKKEN FORMÜLLE TAHMİN (kullanıcı isteği, 2026-10-06)
   Emsal araştırması (hasarsız + onarılmış fiyat) girilmediğinde değer
   kaybı, ekspertizdeki verilerle (parçalar, hasar tutarı, km, piyasa
   değeri) Ek-1 formülünden tahmin edilir. 01.07.2026 sonrası kazalarda
   formül genel şartlardan çıkarıldığı ve mahkeme / tahkim ikinci el
   piyasa farkını esas aldığı için sonuç "formülle tahmin" diye gösterilir.
   Emsal girilince emsal esas alınır. ek1: degerKaybiEk1 sonucu.
   ------------------------------------------------------------------ */
export function degerKaybiFormulle(ek1, { kusurOrani = 100, odenen = 0, hasarTutari = 0 } = {}) {
  const piyasa = round2(Number(ek1?.piyasa) || 0);
  const dk = round2(Number(ek1?.DK) || 0);
  const kusurluTutar = round2(dk * clampYuzde(kusurOrani) / 100);
  const odenenT = round2(Number(odenen) || 0);
  return {
    yontem: 'formul',
    piyasaKaynak: ek1?.piyasaKaynak || '',
    hasarsizAdet: 0,
    hasarliAdet: 0,
    endeks: 1,
    hasarsiz: piyasa,
    hasarli: round2(Math.max(0, piyasa - dk)),
    dk,
    dkOrani: piyasa > 0 ? (dk / piyasa) * 100 : 0,
    hasarOrani: piyasa > 0 && hasarTutari > 0 ? (hasarTutari / piyasa) * 100 : 0,
    kusurOrani: clampYuzde(kusurOrani),
    kusurluTutar,
    odenen: odenenT,
    bakiye: round2(Math.max(0, kusurluTutar - odenenT)),
    eksik: null,
  };
}

/* ------------------------------------------------------------------
   4) ARAÇ MAHRUMİYETİ (İKAME ARAÇ) — trafik sigortası teminatı dışında
   ------------------------------------------------------------------ */
export function mahrumiyetHesapla({
  gunlukKira = 0,          // kaza tarihine göre günlük muadil kira bedeli
  zorunluGider = 0,        // günlük tasarruf (amortisman, yıpranma, yakıt vb.) indirimi
  gun = 0,                 // makul (saf teknik) onarım süresi
  kusurOrani = 100,
} = {}) {
  const net = round2(Math.max(0, (Number(gunlukKira) || 0) - (Number(zorunluGider) || 0)));
  const araToplam = round2(net * (Number(gun) || 0));
  return { net, araToplam, kusurOrani: clampYuzde(kusurOrani), tutar: round2(araToplam * clampYuzde(kusurOrani) / 100) };
}

/* ------------------------------------------------------------------
   5) TARİHLER: 8 iş günü (KTK m.99), zamanaşımı (KTK m.109)
   İş günü: cumartesi, pazar ve resmî tatiller (2429 sayılı Kanun)
   sayılmaz. Arife ve 28 Ekim yarım gündür; sabahı çalışıldığı için iş
   günü sayılır. Dinî bayram günleri Diyanet takviminden (2023-2028);
   tabloda olmayan yılda yalnız sabit tatiller düşülür.
   ------------------------------------------------------------------ */
const tarihOku = (s) => {
  if (!s) return null;
  const [y, m, g] = String(s).slice(0, 10).split('-').map(Number);
  if (!y || !m || !g) return null;
  return new Date(Date.UTC(y, m - 1, g));
};
const isoYaz = (d) => d.toISOString().slice(0, 10);

/** Bugünün tarihi (yerel saatle). toISOString UTC verir; gece 00.00-03.00 arası bir önceki günü yazardı. */
export const bugunISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const SABIT_TATILLER = ['01-01', '04-23', '05-01', '05-19', '07-15', '08-30', '10-29'];
const DINI_BAYRAMLAR = {
  2023: ['04-21', '04-22', '04-23', '06-28', '06-29', '06-30', '07-01'],
  2024: ['04-10', '04-11', '04-12', '06-16', '06-17', '06-18', '06-19'],
  2025: ['03-30', '03-31', '04-01', '06-06', '06-07', '06-08', '06-09'],
  2026: ['03-20', '03-21', '03-22', '05-27', '05-28', '05-29', '05-30'],
  2027: ['03-09', '03-10', '03-11', '05-16', '05-17', '05-18', '05-19'],
  2028: ['02-26', '02-27', '02-28', '05-05', '05-06', '05-07', '05-08'],
};

export const resmiTatilMi = (iso) => {
  const s = String(iso || '').slice(0, 10);
  const ay = s.slice(5);
  return SABIT_TATILLER.includes(ay) || (DINI_BAYRAMLAR[Number(s.slice(0, 4))] || []).includes(ay);
};

/** Başlangıçtan sonraki n'inci iş günü. bayramBilinmiyor: aralıkta dinî bayram tablosu olmayan yıl var. */
export const isGunuEkle = (baslangic, gun = 8) => {
  const d = tarihOku(baslangic);
  if (!d) return null;
  let kalan = gun;
  const atlanan = [];
  while (kalan > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    const g = d.getUTCDay();
    if (g === 0 || g === 6) continue;
    if (resmiTatilMi(isoYaz(d))) { atlanan.push(isoYaz(d)); continue; }
    kalan -= 1;
  }
  const bitis = isoYaz(d);
  return { tarih: bitis, atlanan, bayramBilinmiyor: !DINI_BAYRAMLAR[Number(String(baslangic).slice(0, 4))] || !DINI_BAYRAMLAR[Number(bitis.slice(0, 4))] };
};

export const yilEkle = (s, yil) => {
  const d = tarihOku(s);
  if (!d) return null;
  d.setUTCFullYear(d.getUTCFullYear() + yil);
  return isoYaz(d);
};

export const tarihTR = (s) => {
  if (!s) return '-';
  const [y, m, g] = String(s).slice(0, 10).split('-');
  return y && m && g ? `${g}.${m}.${y}` : String(s);
};

/* ------------------------------------------------------------------
   6) TRAFİK SİGORTASI MADDİ TEMİNAT LİMİTİ (araç başına)
   Poliçenin başlangıç yılına göre. 2024: 200.000 TL, 2025: 300.000 TL,
   2026: 400.000 TL (SEDDK asgari limitleri). 2023 yıl içinde değiştiği
   için ve henüz açıklanmayan yıllar elle girilir.
   ------------------------------------------------------------------ */
export const ZMSS_MADDI_LIMIT = { 2024: 200000, 2025: 300000, 2026: 400000 };

/** Sigortadan istenebilecek tutar (limit dahilinde) ve işletene kalan kısım. */
export function sonucOzeti({ hasarBakiye = 0, dkBakiye = 0, mahrumiyet = 0, limit = 0 } = {}) {
  const sigortaKalemi = round2((Number(hasarBakiye) || 0) + (Number(dkBakiye) || 0));
  const lim = Number(limit) || 0;
  const sigortadan = lim > 0 ? round2(Math.min(sigortaKalemi, lim)) : sigortaKalemi;
  const limitiAsan = lim > 0 ? round2(Math.max(0, sigortaKalemi - lim)) : 0;
  return {
    sigortaKalemi,
    sigortadan,
    limitiAsan,
    mahrumiyet: round2(Number(mahrumiyet) || 0),
    isletenden: round2(limitiAsan + (Number(mahrumiyet) || 0)),
    toplam: round2(sigortaKalemi + (Number(mahrumiyet) || 0)),
  };
}

/* ------------------------------------------------------------------
   DAYANAKLAR — sayfada ve raporda gösterilir
   ------------------------------------------------------------------ */
export const DAYANAKLAR = [
  {
    baslik: 'Değer kaybı = kaza öncesi ve onarım sonrası ikinci el değer farkı',
    metin: 'KTK ZMSS Genel Şartları A.5 (değişik, RG 12.06.2026/33278, yürürlük 01.07.2026): değer kaybı; aracın markası, yaşı, modeli, kullanılmışlık düzeyi, hasar gördüğü kısımlar, geçmiş hasar durumu ile kaza tarihinden önceki ve onarımdan sonraki ikinci el satış değeri arasındaki fark dikkate alınarak tespit edilir. Hasar talebi değer kaybı talebini de kapsar. Eski formül (Ek-1) yürürlükten kaldırıldı.',
    url: 'https://www.resmigazete.gov.tr/eskiler/2026/06/20260612-3.htm',
  },
  {
    baslik: 'Yargıtay: reel (piyasa) yöntemi',
    metin: 'Yargıtay 17. HD 10.05.2016, 2016/966 E. 2016/5728 K.; 4. HD 2021/18000 E. 2021/5904 K.; 4. HD 2021/2451 E. 2021/4771 K. — değer kaybı, kaza tarihindeki hasarsız ikinci el rayiç değer ile onarılmış hâlinin ikinci el rayiç değeri arasındaki farktır; kasko değeri değil serbest piyasa değeri esas alınır.',
  },
  {
    baslik: 'Genel şartlardaki formül mahkemeyi bağlamaz',
    metin: 'Anayasa Mahkemesi 17.07.2020, 2019/40 E. 2020/40 K. (RG 09.10.2020/31269): KTK m.90 ve 92\'deki genel şartlara yapılan atıflar iptal edildi; tazminat TBK haksız fiil hükümlerine göre belirlenir.',
    url: 'https://normkararlarbilgibankasi.anayasa.gov.tr/kbb/ND/2020/40',
  },
  {
    baslik: 'Önceki hasar ve vidalı parçalar',
    metin: 'Aynı bölgede daha önce işlem görmüş parça için mükerrer değer kaybı istenemez; ancak Yargıtay 4. HD 05.11.2024, 2024/3822 E. 2024/10404 K. önceki hasarı ret sebebi değil hesabın unsuru sayar (boyalı yerler ve hasarlı bölgelerin özelliği hesaplanmalı). Boyasız, vidayla sökülüp takılan parçalar (far, cam, jant, plastik tampon) tek başına değer kaybı doğurmaz.',
  },
  {
    baslik: 'Hasar bedeli KDV dahil, parça orijinal',
    metin: 'Yargıtay uygulamasında onarım fatura ibraz edilmese de KDV dahil hesaplanır. Genel Şartlar B.2 (2026 değişik): onarılamayan parça kural olarak orijinali ile değiştirilir; eşdeğer parça için hak sahibinin onayını ispat yükü sigortacıdadır; kıymet artışı tazminattan indirilemez.',
  },
  {
    baslik: 'Araç mahrumiyeti',
    metin: 'Trafik sigortası teminatı dışındadır; kusurlu sürücü, işleten ve malikten istenir. Makul (saf teknik) onarım süresi × günlük muadil kira bedeli; zorunlu gider tasarrufu indirilir (Yargıtay 17. HD 2016/19869 E. 2017/10814 K.; 4. HD 2021/26777 E. 2022/11236 K.).',
  },
  {
    baslik: 'Faiz başlangıcı ve zamanaşımı',
    metin: 'KTK m.99: sigortacı, belgelerle başvurudan itibaren 8 iş günü içinde ödemezse temerrüde düşer (hesapta hafta sonları ve resmî tatiller sayılmaz; arife iş günü sayılır); işleten ve sürücü yönünden faiz kaza tarihinden işler. KTK m.109: tazminat istemi zararı ve tazminat yükümlüsünü öğrenmeden itibaren 2 yılda, her hâlde kazadan itibaren 10 yılda zamanaşımına uğrar (eylem suç ise ceza zamanaşımı).',
  },
  {
    baslik: 'Trafik sigortası maddi teminat limiti',
    metin: 'Araç başına maddi zarar teminatı: 2024 poliçeleri 200.000 TL, 2025 poliçeleri 300.000 TL, 2026 poliçeleri 400.000 TL (SEDDK). Limit poliçenin düzenlendiği döneme göre belirlenir; limiti aşan kısım işletene yöneltilir.',
  },
];
